/**
 * Page liste courtiers — `/brokers` (refonte 9.P.3.C pattern Companies R1+R2).
 *
 * Pattern UX commun :
 *   - sticky page header `top-16 lg:top-0` + filter bar
 *   - table moderne ≥md + cards stack <md
 *   - BrokerStatusBadge cohérent (active emerald / pending amber / disabled red)
 *   - states distincts : loading skeleton / empty / no-match / error retry
 *   - max-w-screen-2xl (anticipation tableaux comparaison)
 *
 * Read-only. Accessible à broker, tenant_admin, super_admin.
 * Le backend filtre par tenant via RLS. Filtres MVP : recherche `q` (full_name /
 * email), filtre `role` (broker / tenant_admin / all), toggle "Inclure désactivés".
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Pagination, usePagination } from "../../components/Pagination";
import { SearchInput } from "../../components/SearchInput";
import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { useBrokers, type BrokerListParams } from "../../lib/queries/brokers";

import { BrokerStatusBadge } from "./_components/BrokerStatusBadge";

const ROLE_OPTIONS: BrokerListParams["role"][] = ["all", "broker", "tenant_admin"];

export function BrokerListPage() {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const [q, setQ] = useState("");
  const [role, setRole] = useState<BrokerListParams["role"]>("all");
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const pagination = usePagination();

  const { data, isLoading, isFetching, error, refetch } = useBrokers({
    cursor: pagination.cursor,
    q: q || undefined,
    role,
    include_disabled: includeDisabled,
  });

  const onSearchChange = (next: string) => {
    setQ(next);
    pagination.reset();
  };
  const onRoleChange = (next: BrokerListParams["role"]) => {
    setRole(next);
    pagination.reset();
  };
  const onToggleDisabled = (next: boolean) => {
    setIncludeDisabled(next);
    pagination.reset();
  };
  const onResetFilters = () => {
    setQ("");
    setRole("all");
    setIncludeDisabled(false);
    pagination.reset();
  };

  const hasFilters = q.trim() !== "" || role !== "all" || includeDisabled;
  const showSkeleton = isLoading && !data;
  const showEmpty = !!data && data.items.length === 0 && !hasFilters && !error;
  const showNoMatch = !!data && data.items.length === 0 && hasFilters && !error;

  return (
    <div className="mx-auto w-full max-w-screen-2xl">
      {/* Sticky page header */}
      <header className="sticky top-16 z-10 -mx-4 border-b border-slate-200 bg-slate-50/95 px-4 py-4 backdrop-blur-sm lg:-mx-8 lg:top-0 lg:px-8 lg:py-5">
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
          {t("brokers.list.title")}
        </h1>
        <p className="mt-1 text-sm text-slate-600">{t("brokers.list.subtitle")}</p>
      </header>

      <div className="px-0 py-6 lg:py-8">
        {/* Filters bar */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <div className="min-w-[16rem] flex-1">
            <SearchInput
              value={q}
              onChange={onSearchChange}
              placeholder={t("brokers.list.search_placeholder")}
            />
          </div>
          <label className="text-sm text-slate-600">
            <span className="sr-only">{t("brokers.list.filters.role")}</span>
            <select
              className="input"
              value={role}
              onChange={(e) => onRoleChange(e.target.value as BrokerListParams["role"])}
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {t(`brokers.list.roles.${r}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={includeDisabled}
              onChange={(e) => onToggleDisabled(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            />
            {t("brokers.list.filters.include_disabled")}
          </label>
        </div>

        {/* Error retry */}
        {error && (
          <div
            role="alert"
            className="mb-6 flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            <span>
              {error instanceof ApiException
                ? getApiErrorMessage(error, lang) || t("errors.generic")
                : t("errors.generic")}
            </span>
            <button
              type="button"
              onClick={() => void refetch()}
              className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
            >
              {t("common.retry")}
            </button>
          </div>
        )}

        {/* Loading skeleton */}
        {showSkeleton && (
          <div
            aria-live="polite"
            className="space-y-2 rounded-xl border border-slate-200 bg-white p-4"
          >
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="h-10 rounded-md bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none"
              />
            ))}
          </div>
        )}

        {/* Empty state */}
        {showEmpty && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center">
            <h2 className="text-base font-semibold text-slate-900">
              {t("brokers.list.empty_state.title")}
            </h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">
              {t("brokers.list.empty_state.subtitle")}
            </p>
          </div>
        )}

        {/* No-match */}
        {showNoMatch && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <strong className="block font-semibold">{t("brokers.list.no_match.title")}</strong>
                <span className="text-amber-700">{t("brokers.list.no_match.subtitle")}</span>
              </div>
              <button
                type="button"
                onClick={onResetFilters}
                className="rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
              >
                {t("brokers.list.no_match.reset")}
              </button>
            </div>
          </div>
        )}

        {/* Table desktop ≥ md */}
        {data && data.items.length > 0 && (
          <>
            <div className="hidden overflow-hidden rounded-xl border border-slate-200 bg-white md:block">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th scope="col" className="px-4 py-3 font-medium">{t("brokers.list.columns.name")}</th>
                    <th scope="col" className="px-4 py-3 font-medium">{t("brokers.list.columns.email")}</th>
                    <th scope="col" className="px-4 py-3 font-medium">{t("brokers.list.columns.role")}</th>
                    <th scope="col" className="px-4 py-3 font-medium">{t("brokers.list.columns.status")}</th>
                    <th scope="col" className="px-4 py-3 font-medium">{t("brokers.list.columns.last_login")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.items.map((b) => (
                    <tr key={b.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <Link
                          to={`/brokers/${b.id}`}
                          className="rounded-sm font-medium text-brand-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
                        >
                          {b.full_name ?? <span className="text-slate-400">—</span>}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{b.email}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
                          {t(`brokers.list.roles.${b.role}`, { defaultValue: b.role })}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <BrokerStatusBadge status={b.status} />
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {b.last_login_at ? (
                          b.last_login_at.slice(0, 10)
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cards stack < md */}
            <ul className="space-y-3 md:hidden">
              {data.items.map((b) => (
                <li
                  key={b.id}
                  className="rounded-xl border border-slate-200 bg-white p-4"
                >
                  <Link
                    to={`/brokers/${b.id}`}
                    className="block rounded-sm text-sm font-medium text-brand-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
                  >
                    {b.full_name ?? b.email}
                  </Link>
                  {b.full_name && (
                    <p className="mt-1 text-xs text-slate-500">{b.email}</p>
                  )}
                  <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                    <div>
                      <dt className="text-slate-500">{t("brokers.list.columns.role")}</dt>
                      <dd className="mt-0.5 text-slate-700">
                        {t(`brokers.list.roles.${b.role}`, { defaultValue: b.role })}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">{t("brokers.list.columns.status")}</dt>
                      <dd className="mt-0.5">
                        <BrokerStatusBadge status={b.status} />
                      </dd>
                    </div>
                  </dl>
                </li>
              ))}
            </ul>

            <div className="mt-6">
              <Pagination
                pageNumber={pagination.pageNumber}
                hasPrev={pagination.hasPrev}
                hasNext={!!data.next_cursor}
                onPrev={pagination.prev}
                onNext={() => pagination.next(data.next_cursor ?? null)}
                disabled={isFetching}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
